import React, { useState } from 'react';
import { USSDMenuItem, useUSSDState } from './ussdState';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface USSDMenuBuilderProps {
  menu: USSDMenuItem;
}

export const USSDMenuBuilder: React.FC<USSDMenuBuilderProps> = ({ menu }) => {
  const { session, navigateTo, goBack, setInputValue, submitInput, endSession } =
    useUSSDState();
  const [inputBuffer, setInputBuffer] = useState('');

  if (!session) return null;

  const currentMenu = session.currentMenu;

  const handleKeyPress = (key: string) => {
    if (currentMenu?.type === 'input') {
      // Handle input mode
      if (key === '0') {
        // Submit
        submitInput();
      } else if (key === '00') {
        // Back
        goBack();
      } else if (key.length === 1 && /[a-zA-Z0-9\s]/.test(key)) {
        // Add character
        const newValue = session.inputValue + key;
        if (
          !currentMenu.inputConfig?.maxLength ||
          newValue.length <= currentMenu.inputConfig.maxLength
        ) {
          setInputValue(newValue);
        }
      }
    } else if (currentMenu?.type === 'menu') {
      // Handle menu navigation
      const choice = parseInt(key, 10);
      
      if (choice === 0) {
        // Exit
        endSession();
      } else if (currentMenu.children && choice <= currentMenu.children.length) {
        const selectedMenu = currentMenu.children[choice - 1];
        
        if (selectedMenu.action) {
          selectedMenu.action();
        }
        
        if (selectedMenu.children || selectedMenu.type !== 'menu') {
          navigateTo(selectedMenu.id, selectedMenu);
        }
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputBuffer(value);
  };

  const handleInputSubmit = () => {
    if (inputBuffer) {
      inputBuffer.split('').forEach((char) => handleKeyPress(char));
      setInputBuffer('');
    }
  };

  return (
    <Card className="p-4 space-y-4 bg-card">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">USSD Menu Builder</h3>
        
        {/* Input Simulator */}
        <div className="flex gap-2">
          <Input
            value={inputBuffer}
            onChange={handleInputChange}
            placeholder="Type keys to simulate..."
            className="font-mono text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInputSubmit();
              }
            }}
          />
          <Button onClick={handleInputSubmit} size="sm">
            Send
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <Button
              key={num}
              variant="outline"
              size="sm"
              onClick={() => handleKeyPress(num.toString())}
              className="font-mono"
            >
              {num}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleKeyPress('00')}
            className="font-mono"
          >
            Back
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleKeyPress('0')}
            className="font-mono"
          >
            {currentMenu?.type === 'input' ? 'Submit' : 'Exit'}
          </Button>
        </div>
      </div>

      {/* Menu Structure Info */}
      <div className="text-xs space-y-1 text-muted-foreground">
        <div>Current: {currentMenu?.label}</div>
        <div>Type: {currentMenu?.type}</div>
        <div>Stack depth: {session.menuStack.length}</div>
      </div>
    </Card>
  );
};
